import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import OrderChat from '../../components/OrderChat'
import { markMessagesAsRead } from '../../lib/chatService'
import { useAuth } from '../../context/AuthContext'

type OrderDetail = {
  id: string
  created_at: string
  total_price: number
  status: string
  payment_status: string
  racket_brand?: string
  racket_description?: string
  racket_owner_name?: string
  tension_vertical?: number
  tension_horizontal?: number
  pickup_address?: string
  delivery_address?: string
  delivery_distance_km?: number
  is_partner_booking?: boolean
  service?: { name: string; base_price: number } | null
  option?: { label: any; price_delta: any } | null
  pickup_mode?: { label: string } | null
  delivery_mode?: { label: string } | null
  profile?: { id: string; email: string; full_name: string; phone?: string } | null
  // Coupon
  coupon_id?: string | null
  original_service_price?: number | null
  user_discount_amount?: number | null
  partner_commission_amount?: number | null
  coupon?: { code: string; type: string } | null
}

const AdminOrderDetailPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [chatReload, setChatReload] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState(false)
  const [notifyOpen, setNotifyOpen] = useState(false)
  const [notifySubject, setNotifySubject] = useState('')
  const [notifyMessage, setNotifyMessage] = useState('')
  const [notifyStatus, setNotifyStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const [notifyError, setNotifyError] = useState<string | null>(null)
  const { profile } = useAuth()

  const loadOrder = useCallback(async () => {
    if (!id) return null
    setLoading(true)
    setError(null)

    const { data, error } = await supabase
      .schema('public')
      .from('orders')
      .select(`
          id,
          created_at,
          total_price,
          status,
          payment_status,
          racket_brand,
          racket_description,
          racket_owner_name,
          tension_vertical,
          tension_horizontal,
          pickup_address,
          delivery_address,
          delivery_distance_km,
          is_partner_booking,
          coupon_id,
          original_service_price,
          user_discount_amount,
          partner_commission_amount,
          coupon:coupons (code, type),
          service:services!fk_orders_service (name, base_price),
          pickup_mode:transport_modes!fk_orders_pickup_mode_id (label),
          delivery_mode:transport_modes!fk_orders_delivery_mode_id (label),
          order_options:order_options (
            option:service_options (label, price_delta)
          ),
          profile: profiles!fk_orders_user (id, email, full_name, phone)
        `)
      .eq('id', id)
      .single()

    if (error) {
      console.error(error)
      setError("Impossible de charger la commande.")
      setLoading(false)
      return null
    }

    // 🧩 mapping correct des relations
    const pickSingle = <T,>(value: T | T[] | null | undefined): T | null => {
      if (!value) return null
      if (Array.isArray(value)) return value[0] ?? null
      return value
    }

    const service = pickSingle(data.service)
    const option = pickSingle(data.order_options)?.option
    const pickupMode = pickSingle(data.pickup_mode)
    const deliveryMode = pickSingle(data.delivery_mode)
    const profileData = pickSingle(data.profile)

    const mapped: OrderDetail = {
      id: data.id,
      created_at: data.created_at,
      total_price: data.total_price,
      status: data.status,
      payment_status: data.payment_status,
      racket_brand: data.racket_brand,
      racket_description: data.racket_description,
      racket_owner_name: data.racket_owner_name,
      tension_vertical: data.tension_vertical,
      tension_horizontal: data.tension_horizontal,

      pickup_address: data.pickup_address,
      delivery_address: data.delivery_address,
      delivery_distance_km: data.delivery_distance_km,
      is_partner_booking: data.is_partner_booking ?? false,
      coupon_id: data.coupon_id ?? null,
      original_service_price: data.original_service_price != null ? Number(data.original_service_price) : null,
      user_discount_amount: data.user_discount_amount != null ? Number(data.user_discount_amount) : null,
      partner_commission_amount: data.partner_commission_amount != null ? Number(data.partner_commission_amount) : null,
      coupon: pickSingle(data.coupon) ?? null,
      service,
      option: pickSingle(option),
      pickup_mode: pickupMode,
      delivery_mode: deliveryMode,
      profile: profileData,
    }
    setOrder(mapped)
    setLoading(false)
    return mapped
  }, [id])

  useEffect(() => {
    if (!order) return
    markMessagesAsRead(order.id, profile?.id ?? '')
  }, [order?.id, profile?.id])

  useEffect(() => {
    if (!order) return
    setNotifySubject(`Commande #${order.id} - Statut: ${order.status}`)
  }, [order?.id, order?.status])

  useEffect(() => {
    loadOrder()
  }, [loadOrder])

  const updateOrder = async (fields: Partial<OrderDetail>) => {
    if (!id || !order) return

    const previous = order
    setOrder(prev => (prev ? { ...prev, ...fields } : prev))
    setUpdating(true)

    const { data, error } = await supabase.functions.invoke('admin-update-order', {
      body: { orderId: id, ...fields },
    })

    if (error || data?.error) {
      console.error(error || data?.error)

      // Fallback direct update via Supabase REST (permet de continuer les tests même si la fonction edge échoue)
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('orders')
        .update(fields)
        .eq('id', id)
        .select('id, status, payment_status, total_price, racket_brand, racket_description, tension_vertical, tension_horizontal, pickup_address, delivery_address, delivery_distance_km')
        .single()

      if (!fallbackError && fallbackData) {
        setOrder(prev => (prev ? { ...prev, ...fallbackData } : prev))
        setUpdating(false)
        alert('Commande mise à jour ✅')
        return
      }

      await loadOrder()
      setUpdating(false)
      alert("Erreur lors de la mise à jour.")
      return
    }

    if (data?.order) {
      setOrder(prev => (prev ? { ...prev, ...data.order } : prev))
      if (Array.isArray(data.createdMessages) && data.createdMessages.length > 0) {
        setChatReload(prev => prev + 1)
      }
    } else {
      await loadOrder()
    }

    setUpdating(false)
    alert("Commande mise à jour ✅")
  }

  const sendOrderUpdateEmail = async () => {
    if (!order?.id) return
    setNotifyStatus('sending')
    setNotifyError(null)

    const { data, error } = await supabase.functions.invoke('send-order-update-email', {
      body: {
        orderId: order.id,
        subject: notifySubject || `Commande #${order.id} - Statut: ${order.status}`,
        message: notifyMessage,
      },
    })

    if (error) {
      console.error(error)
      setNotifyError(error.message || "Impossible d'envoyer l'email.")
      setNotifyStatus('error')
      return
    }

    if (data?.error) {
      setNotifyError(typeof data.error === 'string' ? data.error : "Impossible d'envoyer l'email.")
      setNotifyStatus('error')
      return
    }

    setNotifyStatus('success')
  }

  if (loading) return <p>Chargement...</p>
  if (error || !order)
    return (
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">Commande introuvable</h1>
        </div>
        <button className="btn btn-primary" data-testid="admin-order-back" onClick={() => navigate('/admin/orders')}>
          Retour à la liste
        </button>
      </div>
    )

  return (
    <div className="card">
      <div className="card-header" style={{ justifyContent: 'space-between' }}>
        <h1 className="card-title">Commande #{order.id}</h1>
        <button className="btn btn-ghost" data-testid="admin-order-back-top" onClick={() => navigate('/admin/orders')}>
          ← Retour
        </button>
      </div>

      {/* Client */}
      <section style={{ marginTop: '1rem' }}>
        <h2 style={{ fontWeight: 600 }}>Client</h2>
        <p>
          <strong>Nom :</strong> {order.profile?.full_name || '—'}
        </p>
        <p>
          <strong>Email :</strong> {order.profile?.email || '—'}
        </p>
        {order.profile?.phone && (
          <p>
            <strong>Téléphone :</strong> {order.profile.phone}
          </p>
        )}
      </section>

      {/* Service */}
      <section style={{ marginTop: '1rem' }}>
        <h2 style={{ fontWeight: 600 }}>Service</h2>
        <p>
          <strong>{order.service?.name}</strong> ({order.total_price.toFixed(2)} CHF)
        </p>
        {order.option && (
          <p>
            Option : {order.option.label} ({order.option.price_delta >= 0 ? '+' : ''}
            {order.option.price_delta} CHF)
          </p>
        )}
      </section>

      {/* Raquette */}
      <section style={{ marginTop: '1rem' }}>
        <h2 style={{ fontWeight: 600 }}>Raquette</h2>
        {order.racket_owner_name && (
          <p><strong>Propriétaire :</strong> {order.racket_owner_name}</p>
        )}
        <p>
          {order.racket_brand} {order.racket_description && `— ${order.racket_description}`}
        </p>
        <p>
          Tension : {order.tension_vertical} kg / {order.tension_horizontal} kg
        </p>
      </section>

      {/* Livraison */}
      <section style={{ marginTop: '1rem' }}>
        <h2 style={{ fontWeight: 600 }}>Prise en charge & Livraison</h2>
        <p>
          <strong>Prise en charge :</strong> {order.pickup_mode?.label || '—'}
        </p>
        {order.pickup_address && <p>Adresse pickup : {order.pickup_address}</p>}
        <p>
          <strong>Livraison :</strong> {order.delivery_mode?.label || '—'}
        </p>
        {order.delivery_address && <p>Adresse livraison : {order.delivery_address}</p>}
        {order.delivery_distance_km && (
          <p>Distance : {order.delivery_distance_km.toFixed(1)} km</p>
        )}
      </section>

      {/* Statuts */}
      <section style={{ marginTop: '1rem' }}>
        <h2 style={{ fontWeight: 600 }}>Statuts</h2>
        <p>
          <strong>Statut commande :</strong> {order.status}
        </p>
        <p>
          <strong>Paiement :</strong>{' '}
          {order.payment_status === 'PAID' ? (
            <span style={{ color: '#22c55e', fontWeight: 600 }}>Payé</span>
          ) : (
            <span style={{ color: '#facc15', fontWeight: 600 }}>En attente</span>
          )}
        </p>
        <div style={{ marginTop: '.75rem' }}>
          <button
            className="btn btn-primary"
            data-testid="admin-order-toggle-payment"
            style={{ marginRight: '.5rem' }}
            onClick={() =>
              updateOrder({
                payment_status:
                  order.payment_status === 'PAID' ? 'OFFLINE_PENDING' : 'PAID',
              })
            }
            disabled={updating}
          >
            {order.payment_status === 'PAID'
              ? 'Marquer en attente'
              : 'Marquer comme payée'}
          </button>

          <select
            data-testid="admin-order-status-select"
            value={order.status}
            onChange={e => updateOrder({ status: e.target.value })}
            disabled={updating}
            style={{
              padding: '.4rem',
              borderRadius: '.4rem',
              background: '#1e293b',
              color: 'white',
            }}
          >
            <option value="EN_ATTENTE">EN_ATTENTE</option>
            <option value="EN_COURS">EN_COURS</option>
            <option value="PRET_A_RECUPERER">PRET_A_RECUPERER</option>
            <option value="EN_LIVRAISON">EN_LIVRAISON</option>
            <option value="LIVREE">LIVREE</option>
            <option value="ANNULEE">ANNULEE</option>
          </select>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setNotifyOpen((open) => {
                const next = !open
                if (next) {
                  setNotifyStatus('idle')
                  setNotifyError(null)
                }
                return next
              })
            }}
            disabled={!order.profile?.email}
            data-testid="admin-order-notify-toggle"
          >
            📧 Notifier le client
          </button>
          {!order.profile?.email && <span style={{ color: '#ef4444' }}>Email du client manquant</span>}
          {notifyStatus === 'success' && <span style={{ color: '#22c55e' }} data-testid="admin-order-notify-success">Email envoyé</span>}
          {notifyStatus === 'error' && notifyError && <span style={{ color: '#ef4444' }} data-testid="admin-order-notify-error">{notifyError}</span>}
        </div>

        {notifyOpen && (
          <div
            className="card"
            style={{ marginTop: '.75rem', padding: '1rem', background: '#0f172a', border: '1px solid rgba(148,163,184,0.3)' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <label style={{ fontWeight: 600 }}>Sujet</label>
              <input
                type="text"
                value={notifySubject}
                onChange={(e) => setNotifySubject(e.target.value)}
                style={{ padding: '.6rem', borderRadius: '.5rem', border: '1px solid #475569', background: '#1e293b', color: 'white' }}
                placeholder={`Commande #${order.id} - Statut: ${order.status}`}
                data-testid="admin-order-notify-subject"
              />

              <label style={{ fontWeight: 600 }}>Message au client</label>
              <textarea
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                rows={5}
                style={{ padding: '.6rem', borderRadius: '.5rem', border: '1px solid #475569', background: '#1e293b', color: 'white' }}
                placeholder="Donnez des details utiles sur l'avancement de la commande."
                data-testid="admin-order-notify-message"
              />

              {notifyStatus === 'sending' && <span style={{ color: '#eab308' }}>Envoi en cours...</span>}
                {notifyStatus === 'error' && notifyError && <span style={{ color: '#ef4444' }} data-testid="admin-order-notify-error-inline">{notifyError}</span>}
                {notifyStatus === 'success' && <span style={{ color: '#22c55e' }} data-testid="admin-order-notify-success-inline">Email envoyé avec succès</span>}

              <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={sendOrderUpdateEmail}
                  disabled={!order.profile?.email || notifyStatus === 'sending' || notifySubject.trim() === ''}
                    data-testid="admin-order-notify-send"
                >
                  {notifyStatus === 'sending' ? 'Envoi...' : 'Envoyer'}
                </button>
                <button className="btn btn-ghost" onClick={() => setNotifyOpen(false)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Coupon / Réduction */}
      {order.coupon_id && order.coupon && (
        <section
          data-testid="admin-order-coupon"
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '0.5rem',
          }}
        >
          <h2 style={{ fontWeight: 600, color: '#22c55e', marginBottom: '0.5rem' }}>
            🎟️ Coupon appliqué
          </h2>
          <p>
            <strong>Code :</strong> {order.coupon.code}
            <span style={{ marginLeft: '0.5rem', fontSize: '.85rem', color: '#9ca3af' }}>({order.coupon.type})</span>
          </p>
          {order.original_service_price != null && (
            <p>
              <strong>Prix service original :</strong>{' '}
              <s style={{ color: '#9ca3af' }}>{order.original_service_price.toFixed(2)} CHF</s>
            </p>
          )}
          {order.user_discount_amount != null && order.user_discount_amount > 0 && (
            <p>
              <strong>Réduction utilisateur :</strong>{' '}
              <span style={{ color: '#22c55e', fontWeight: 600 }}>-{order.user_discount_amount.toFixed(2)} CHF</span>
              {order.original_service_price != null && order.original_service_price > 0 && (
                <span style={{ color: '#9ca3af', marginLeft: '.5rem' }}>
                  ({((order.user_discount_amount / order.original_service_price) * 100).toFixed(0)}%)
                </span>
              )}
            </p>
          )}
          {order.partner_commission_amount != null && order.partner_commission_amount > 0 && (
            <p>
              <strong>Commission partenaire :</strong>{' '}
              <span style={{ color: '#facc15', fontWeight: 600 }}>{order.partner_commission_amount.toFixed(2)} CHF</span>
            </p>
          )}
          {order.original_service_price != null && (order.user_discount_amount ?? 0) > 0 && (
            <p>
              <strong>Prix service après réduction :</strong>{' '}
              <span style={{ fontWeight: 600 }}>{(order.original_service_price - (order.user_discount_amount ?? 0)).toFixed(2)} CHF</span>
            </p>
          )}
        </section>
      )}

      <div
        style={{
          borderTop: '1px solid rgba(148,163,184,0.3)',
          marginTop: '1.5rem',
          paddingTop: '1rem',
          fontSize: '1rem',
          color: '#22c55e',
        }}
      >
        Total : {order.total_price.toFixed(2)} CHF
      </div>
      {/* 💬 CHAT */}
      <section style={{ marginTop: '2rem' }}>
        <h2>Messages avec <span style={{ color: '#22c55e' }}>{order.profile?.full_name || order.profile?.email}</span></h2>
        <OrderChat orderId={order.id} orderStatus={order.status} refreshKey={chatReload}/>
      </section>
    </div>
  )
}

export default AdminOrderDetailPage
